import { useState } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import { format } from 'date-fns'

import { invoicingService } from '@/api/invoicing'
import { get_clients } from '@/api/client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/date-picker'
import { useToast } from '@/hooks/use-toast'

const decimalStr = z
  .string()
  .regex(/^\d+(\.\d{0,4})?$/, { message: 'Nombre décimal invalide' })

const lineSchema = z.object({
  description: z.string().min(1, { message: 'Requis' }),
  quantity:    decimalStr.default('1.00'),
  unit_price:  decimalStr,
  tva_active:  z.boolean().default(false),
  tva_rate:    decimalStr.default('20.00'),
})

const createInvoiceSchema = z.object({
  client:         z.string().min(1, { message: 'Sélectionnez un client' }),
  issue_date:     z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Date requise' }),
  due_date:       z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  notes:          z.string().nullable().optional(),
  ras_tva_active: z.boolean().default(false),
  ras_tva_rate:   decimalStr.default('75.00'),
  ras_ir_active:  z.boolean().default(false),
  ras_ir_rate:    decimalStr.default('11.00'),
  lines: z.array(lineSchema).min(1, { message: 'Au moins une ligne' }),
})

type FormValues = z.infer<typeof createInvoiceSchema>

interface InvoiceCreateDialogProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
}

function computeTotals(values: FormValues) {
  let ht = 0
  let tva = 0
  for (const l of values.lines) {
    const qty = parseFloat(l.quantity || '0')
    const up = parseFloat(l.unit_price || '0')
    const lineHt = qty * up
    const lineTva = l.tva_active ? lineHt * (parseFloat(l.tva_rate || '20') / 100) : 0
    ht += lineHt
    tva += lineTva
  }
  const ttc = ht + tva
  const rassTva = values.ras_tva_active ? tva * (parseFloat(values.ras_tva_rate || '75') / 100) : 0
  const rassIr  = values.ras_ir_active  ? ht * (parseFloat(values.ras_ir_rate || '11') / 100) : 0
  const net = ttc - rassTva - rassIr
  return { ht, tva, ttc, rassTva, rassIr, net }
}

const fmt = (v: number) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(v)

export function InvoiceCreateDialog({ open, onClose, onCreated }: InvoiceCreateDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [submitting, setSubmitting] = useState(false)

  const { data: clientsData } = useQuery({
    queryKey: ['clients-select'],
    queryFn: () => get_clients({ page: 0, page_size: 100, ordering: null, name: null }),
    enabled: open,
  })

  const { data: settings } = useQuery({
    queryKey: ['invoicing-settings'],
    queryFn: invoicingService.getSettings,
    enabled: open,
  })

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(createInvoiceSchema),
    defaultValues: {
      issue_date:     format(new Date(), 'yyyy-MM-dd'),
      ras_tva_active: false,
      ras_tva_rate:   '75.00',
      ras_ir_active:  false,
      ras_ir_rate:    '11.00',
      lines: [{ description: '', quantity: '1.00', unit_price: '', tva_active: true, tva_rate: settings?.default_tva_rate ?? '20.00' }],
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'lines' })

  const values = watch()
  const totals = computeTotals(values)

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true)
    try {
      await invoicingService.create({
        client:         data.client,
        issue_date:     data.issue_date,
        due_date:       data.due_date || null,
        notes:          data.notes || null,
        ras_tva_active: data.ras_tva_active,
        ras_tva_rate:   data.ras_tva_rate,
        ras_ir_active:  data.ras_ir_active,
        ras_ir_rate:    data.ras_ir_rate,
        lines: data.lines.map((l, i) => ({
          description: l.description,
          quantity:    l.quantity,
          unit_price:  l.unit_price,
          tva_active:  l.tva_active,
          tva_rate:    l.tva_rate,
          ordering:    i,
        })),
      })
      toast({ title: t('inv_create'), description: t('inv_form_submit') })
      reset()
      onCreated()
    } catch {
      toast({ title: t('error'), description: t('error_generic'), variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const rasTvaActive = watch('ras_tva_active')
  const rasIrActive  = watch('ras_ir_active')

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle>{t('inv_create')}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[75vh]">
          <form id="invoice-create-form" onSubmit={handleSubmit(onSubmit)} className="px-6 py-4 space-y-6">
            {/* Client + Dates */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t('inv_form_client')} *</Label>
                <Controller
                  name="client"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <SelectTrigger className={errors.client ? 'border-destructive' : ''}>
                        <SelectValue placeholder={t('inv_form_client')} />
                      </SelectTrigger>
                      <SelectContent>
                        {clientsData?.results.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.client && (
                  <p className="text-xs text-destructive">{errors.client.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>{t('inv_form_issue_date')} *</Label>
                <Controller
                  name="issue_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      date={field.value ? new Date(field.value) : undefined}
                      setDate={(d) => setValue('issue_date', d ? format(d, 'yyyy-MM-dd') : '')}
                      showYearNavigation={false}
                    />
                  )}
                />
                {errors.issue_date && (
                  <p className="text-xs text-destructive">{errors.issue_date.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>{t('inv_form_due_date')}</Label>
                <Controller
                  name="due_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      date={field.value ? new Date(field.value) : undefined}
                      setDate={(d) =>
                        setValue('due_date', d ? format(d, 'yyyy-MM-dd') : null)
                      }
                      showYearNavigation={false}
                    />
                  )}
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t('inv_form_notes')}</Label>
                <Input {...register('notes')} placeholder={t('inv_form_notes')} />
              </div>
            </div>

            {/* RAS section */}
            <div className="rounded-lg border p-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-4">
                  <Controller
                    name="ras_tva_active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="ras-tva"
                      />
                    )}
                  />
                  <Label htmlFor="ras-tva">{t('inv_form_ras_tva')}</Label>
                  {rasTvaActive && (
                    <div className="flex items-center gap-1">
                      <Input
                        {...register('ras_tva_rate')}
                        className="w-20 h-8 text-sm"
                        placeholder="75.00"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <Controller
                    name="ras_ir_active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="ras-ir"
                      />
                    )}
                  />
                  <Label htmlFor="ras-ir">{t('inv_form_ras_ir')}</Label>
                  {rasIrActive && (
                    <div className="flex items-center gap-1">
                      <Input
                        {...register('ras_ir_rate')}
                        className="w-20 h-8 text-sm"
                        placeholder="11.00"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Lines */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">{t('inv_form_lines')}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    append({
                      description: '',
                      quantity:    '1.00',
                      unit_price:  '',
                      tva_active:  true,
                      tva_rate:    settings?.default_tva_rate ?? '20.00',
                    })
                  }
                >
                  <Plus className="me-1 h-4 w-4" />
                  {t('inv_form_add_line')}
                </Button>
              </div>

              {errors.lines?.root && (
                <p className="text-xs text-destructive">{errors.lines.root.message}</p>
              )}

              <div className="space-y-2">
                {/* Header row */}
                <div className="hidden sm:grid sm:grid-cols-[1fr_6rem_7rem_5rem_4rem_2rem] gap-2 px-1">
                  <span className="text-xs text-muted-foreground font-medium">{t('inv_line_description')}</span>
                  <span className="text-xs text-muted-foreground font-medium text-right">{t('inv_line_qty')}</span>
                  <span className="text-xs text-muted-foreground font-medium text-right">{t('inv_line_unit_price')}</span>
                  <span className="text-xs text-muted-foreground font-medium text-right">{t('inv_line_tva')}</span>
                  <span className="text-xs text-muted-foreground font-medium text-center">{t('inv_line_tva')} %</span>
                  <span />
                </div>

                {fields.map((field, index) => (
                  <div key={field.id} className="grid sm:grid-cols-[1fr_6rem_7rem_5rem_4rem_2rem] gap-2 items-center rounded-lg border p-2">
                    <Input
                      {...register(`lines.${index}.description`)}
                      placeholder={t('inv_line_description')}
                      className="h-8 text-sm"
                    />
                    <Input
                      {...register(`lines.${index}.quantity`)}
                      placeholder="1.00"
                      className="h-8 text-sm text-right"
                    />
                    <Input
                      {...register(`lines.${index}.unit_price`)}
                      placeholder="0.00"
                      className="h-8 text-sm text-right"
                    />
                    <div className="flex justify-center">
                      <Controller
                        name={`lines.${index}.tva_active`}
                        control={control}
                        render={({ field: f }) => (
                          <Switch checked={f.value} onCheckedChange={f.onChange} />
                        )}
                      />
                    </div>
                    <Input
                      {...register(`lines.${index}.tva_rate`)}
                      placeholder="20.00"
                      className="h-8 text-sm text-right"
                      disabled={!watch(`lines.${index}.tva_active`)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals preview */}
            <div className="rounded-lg border bg-muted/30 p-4 space-y-1.5">
              <p className="text-sm font-semibold mb-2">{t('inv_total_preview')}</p>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('inv_totals_ht')}</span>
                <span>{fmt(totals.ht)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('inv_totals_tva')}</span>
                <span>{fmt(totals.tva)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span className="text-muted-foreground">{t('inv_totals_ttc')}</span>
                <span>{fmt(totals.ttc)}</span>
              </div>
              {rasTvaActive && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>- {t('inv_totals_ras_tva')}</span>
                  <span>- {fmt(totals.rassTva)}</span>
                </div>
              )}
              {rasIrActive && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>- {t('inv_totals_ras_ir')}</span>
                  <span>- {fmt(totals.rassIr)}</span>
                </div>
              )}
              <Separator className="my-1" />
              <div className="flex justify-between text-base font-bold">
                <span>{t('inv_totals_net')}</span>
                <span className="text-primary">{fmt(totals.net)}</span>
              </div>
            </div>
          </form>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            {t('cancel')}
          </Button>
          <Button form="invoice-create-form" type="submit" disabled={submitting}>
            {submitting ? t('loading') : t('inv_form_submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
